import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import type { AppDispatch, RootState } from "./store";

/* ------------------------------------------- */
/*                   TYPES                     */
/* ------------------------------------------- */

export interface Merchant {
  merchantId: string;   // == Firestore doc ID
  userId: string;        // owner

  email?: string;
  businessName?: string;
  businessType?: string;
  country?: string;
  currency?: string;
  website?: string | null;
  phone?: string | null;
  status?: "active" | "pending" | "suspended";
  kycVerified?: boolean;

  joinDate: string | null;
  updatedAt: string | null;

  [key: string]: any;
}

/* Mirrors merchantTransactions/{merchantId}/transactions/{intentId}
   written by the payment gateway (createCheckoutSession /
   createPaymentIntent / the confirm handlers). Read directly off
   Firestore — same client-read pattern storeOrdersSlice uses for
   ProductOrders — instead of going through a key-gated endpoint. */
export interface MerchantTransaction {
  id: string;
  object: string;
  merchant_id: string;
  amount: number;
  amount_received: number;
  currency: string;
  status: string;
  payment_method_types: string[];
  description: string | null;
  customer_email: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_country: string | null;
  mode: "test" | "live";
  b2b_fee: number;
  merchant_net: number;
  created: number;
  created_at: string | null;
  [key: string]: any;
}

interface MerchantState {
  merchants: Merchant[];       // owner-scoped only — written by listenToUserMerchants / fetchUserMerchants
  merchantsLoaded: boolean;
  currentMerchant: Merchant | null;

  transactions: MerchantTransaction[];   // scoped to currentMerchant — written by fetchMerchantTransactions / listenToMerchantTransactions
  transactionsLoaded: boolean;

  loading: boolean;
  error: string | null;
}

const initialState: MerchantState = {
  merchants: [],
  merchantsLoaded: false,
  currentMerchant: null,

  transactions: [],
  transactionsLoaded: false,

  loading: false,
  error: null,
};

/* ------------------------------------------- */
/*              FIREBASE INIT                  */
/* ------------------------------------------- */

const db = getFirestore();

/* ------------------------------------------- */
/*                 PARSER                      */
/* ------------------------------------------- */

const toISO = (ts: any): string | null =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseMerchant = (id: string, raw: Record<string, any>): Merchant => ({
  merchantId: id,
  userId: raw.userId ?? "",
  email: raw.email ?? "",
  businessName: raw.businessName ?? "",
  businessType: raw.businessType ?? "individual",
  country: raw.country ?? "",
  currency: raw.currency ?? "eur",
  website: raw.website ?? null,
  phone: raw.phone ?? null,
  status: raw.status ?? "active",
  kycVerified: raw.kycVerified ?? false,
  joinDate: toISO(raw.joinDate),
  updatedAt: toISO(raw.updatedAt),
});

const parseTransaction = (id: string, raw: Record<string, any>): MerchantTransaction => ({
  id,
  object:               raw.object               ?? "payment_intent",
  merchant_id:          raw.merchant_id          ?? "",
  amount:               raw.amount               ?? 0,
  amount_received:      raw.amount_received      ?? 0,
  currency:             raw.currency             ?? "eur",
  status:               raw.status               ?? "pending",
  payment_method_types: raw.payment_method_types ?? ["card"],
  description:          raw.description          ?? null,
  customer_email:       raw.customer_email       ?? null,
  customer_name:        raw.customer_name        ?? null,
  customer_phone:       raw.customer_phone       ?? null,
  customer_country:     raw.customer_country     ?? null,
  mode:                 raw.mode                 ?? "test",
  b2b_fee:              raw.b2b_fee              ?? 0,
  merchant_net:         raw.merchant_net         ?? 0,
  created:              raw.created              ?? 0,
  created_at:           toISO(raw.created_at),
  ...raw,
});

/* ------------------------------------------- */
/*              UPSERT MERCHANT                 */
/* ------------------------------------------- */

export const upsertMerchant = createAsyncThunk<
  Merchant,
  { merchantId?: string; data: Partial<Merchant> & { userId: string } }
>("merchant/upsertMerchant", async ({ merchantId, data }) => {
  const colRef = collection(db, "merchants");

  const clean = (obj: any) => {
    const out: any = {};
    Object.keys(obj).forEach((k) => {
      if (obj[k] !== undefined) out[k] = obj[k];
    });
    return out;
  };

  const updateBase = { ...clean(data), updatedAt: serverTimestamp() };

  /* ---------- UPDATE ---------- */
  if (merchantId) {
    const refDoc = doc(colRef, merchantId);
    await updateDoc(refDoc, updateBase);

    const snap = await getDoc(refDoc);
    return parseMerchant(snap.id, snap.data() || {});
  }

  /* ---------- CREATE ---------- */
  const createBase = {
    ...updateBase,
    joinDate: new Date().toISOString(),
    status: "active",
    kycVerified: false,
    createdAt: serverTimestamp(),
  };

  const newRef = await addDoc(colRef, createBase);
  await updateDoc(newRef, { merchantId: newRef.id });

  const snap = await getDoc(newRef);
  return parseMerchant(newRef.id, snap.data() || {});
});

/* ------------------------------------------- */
/*              FETCH MERCHANT DATA             */
/* ------------------------------------------- */

export const fetchMerchantData = createAsyncThunk<Merchant, string>(
  "merchant/fetchMerchantData",
  async (merchantId, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, "merchants", merchantId));
      if (!snap.exists()) return rejectWithValue("Merchant not found");
      return parseMerchant(snap.id, snap.data());
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchant");
    }
  }
);

/* ------------------------------------------- */
/*         LISTEN TO USER MERCHANTS             */
/* ------------------------------------------- */

let userMerchantsUnsub: null | (() => void) = null;

export const listenToUserMerchants =
  (userId: string) => (dispatch: any) => {
    if (userMerchantsUnsub) { userMerchantsUnsub(); userMerchantsUnsub = null; }
    if (!userId) { dispatch(setMerchants([])); return () => {}; }

    const q = query(
      collection(db, "merchants"),
      where("userId", "==", userId)
    );

    userMerchantsUnsub = onSnapshot(
      q,
      (snap) => {
        dispatch(setMerchants(snap.docs.map((d) => parseMerchant(d.id, d.data()))));
      },
      (err) => {
        console.error("🔥 listenToUserMerchants:", err);
        dispatch(setMerchants([]));
      }
    );

    /* returned so callers can detach on unmount — the module-level
       handle alone leaked the previous user's listener across logout */
    return () => {
      if (userMerchantsUnsub) { userMerchantsUnsub(); userMerchantsUnsub = null; }
    };
  };

/* ------------------------------------------- */
/*         FETCH USER MERCHANTS (one-shot)      */
/* ------------------------------------------- */

export const fetchUserMerchants = createAsyncThunk<Merchant[], string>(
  "merchant/fetchUserMerchants",
  async (userId, { rejectWithValue }) => {
    if (!userId) return rejectWithValue("User ID is required");

    try {
      const q = query(collection(db, "merchants"), where("userId", "==", userId));
      const snap = await getDocs(q);

      return snap.docs
        .map((d) => parseMerchant(d.id, d.data()))
        .sort((a, b) => {
          if (!a.joinDate || !b.joinDate) return 0;
          return new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime();
        });
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch merchants");
    }
  }
);

/* ------------------------------------------- */
/*         FETCH MERCHANT TRANSACTIONS          */
/*         (one-shot — mirrors fetchStoreHostOrders)   */
/* ------------------------------------------- */

export const fetchMerchantTransactions = createAsyncThunk<
  MerchantTransaction[],
  { merchantId: string; maxResults?: number }
>(
  "merchant/fetchMerchantTransactions",
  async ({ merchantId, maxResults = 100 }, { rejectWithValue }) => {
    if (!merchantId) return rejectWithValue("merchantId is required");

    try {
      const q = query(
        collection(db, "merchantTransactions", merchantId, "transactions"),
        orderBy("created", "desc"),
        limit(maxResults)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => parseTransaction(d.id, d.data()));
    } catch (e: any) {
      return rejectWithValue(e.message || "Failed to fetch transactions");
    }
  }
);

/* ------------------------------------------- */
/*       LISTEN TO MERCHANT TRANSACTIONS        */
/*       (realtime — mirrors listenToStoreHostOrders)  */
/* ------------------------------------------- */

let merchantTransactionsUnsub: null | (() => void) = null;

export const listenToMerchantTransactions =
  (merchantId: string, maxResults = 100) => (dispatch: any) => {
    if (merchantTransactionsUnsub) { merchantTransactionsUnsub(); merchantTransactionsUnsub = null; }
    if (!merchantId) { dispatch(setTransactions([])); return () => {}; }

    const q = query(
      collection(db, "merchantTransactions", merchantId, "transactions"),
      orderBy("created", "desc"),
      limit(maxResults)
    );

    merchantTransactionsUnsub = onSnapshot(
      q,
      (snap) => {
        dispatch(setTransactions(snap.docs.map((d) => parseTransaction(d.id, d.data()))));
      },
      (err) => {
        console.error("🔥 listenToMerchantTransactions:", err);
        dispatch(setTransactions([]));
      }
    );

    /* returned so callers can detach on unmount — same reasoning as
       listenToUserMerchants: a module-level handle alone leaks the
       previous merchant's listener when the user switches accounts */
    return () => {
      if (merchantTransactionsUnsub) { merchantTransactionsUnsub(); merchantTransactionsUnsub = null; }
    };
  };



const merchantSlice = createSlice({
  name: "merchant",
  initialState,
  reducers: {
    setMerchants: (state, a: PayloadAction<Merchant[]>) => {
      state.merchants = a.payload;
      state.merchantsLoaded = true;
    },
    setCurrentMerchant: (state, a: PayloadAction<Merchant | null>) => {
      state.currentMerchant = a.payload;
    },
    setTransactions: (state, a: PayloadAction<MerchantTransaction[]>) => {
      state.transactions = a.payload;
      state.transactionsLoaded = true;
    },
    clearMerchants: (state) => {
      state.merchants = [];
      state.merchantsLoaded = false;
      state.currentMerchant = null;
      state.transactions = [];
      state.transactionsLoaded = false;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (b) => {
    // ✅ ALL addCase FIRST
    b.addCase(upsertMerchant.fulfilled, (state, a) => {
      const i = state.merchants.findIndex((m) => m.merchantId === a.payload.merchantId);
      if (i !== -1) state.merchants[i] = a.payload;
      else state.merchants.push(a.payload);

      state.currentMerchant = a.payload;
      state.loading = false;
    });

    b.addCase(fetchMerchantData.fulfilled, (state, a) => {
      state.currentMerchant = a.payload;
      state.loading = false;
    });

    b.addCase(fetchUserMerchants.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    b.addCase(fetchUserMerchants.fulfilled, (state, a) => {
      state.merchants = a.payload;
      state.merchantsLoaded = true;
      state.loading = false;
    });

    b.addCase(fetchUserMerchants.rejected, (state, a: any) => {
      state.loading = false;
      state.error = a.payload || "Failed to fetch merchants";
    });

    b.addCase(fetchMerchantTransactions.fulfilled, (state, a) => {
      state.transactions = a.payload;
      state.transactionsLoaded = true;
      state.loading = false;
    });

    b.addCase(fetchMerchantTransactions.rejected, (state, a: any) => {
      state.loading = false;
      state.error = a.payload || "Failed to fetch transactions";
    });

    // ✅ THEN addMatcher LAST
    b.addMatcher(
      (a) => a.type.startsWith("merchant/") && a.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );

    b.addMatcher(
      (a) => a.type.startsWith("merchant/") && a.type.endsWith("/rejected"),
      (state, a: any) => {
        state.loading = false;
        state.error = a.payload || "Something went wrong";
      }
    );
  },
});

export const { setMerchants, setCurrentMerchant, setTransactions, clearMerchants, setError } =
  merchantSlice.actions;

export default merchantSlice.reducer;

/* ------------------------------------------- */
/*                  SELECTORS                   */
/* ------------------------------------------- */

export const selectMerchants        = (s: RootState) => s.merchant.merchants;
export const selectMerchantLoading  = (s: RootState) => s.merchant.loading;
export const selectMerchantError    = (s: RootState) => s.merchant.error;
export const selectTransactions        = (s: RootState) => s.merchant.transactions;
export const selectTransactionsLoaded  = (s: RootState) => s.merchant.transactionsLoaded;